// src/components/ReferralPanel.js
// Every member gets a unique referral link
// Referrals tracked in Supabase
import { useState, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import './ReferralPanel.css'

const BASE_URL = window.location.origin

export default function ReferralPanel({ profile, tier }) {
  const { session } = useAuth()
  const [referrals, setReferrals] = useState([])
  const [copied, setCopied] = useState(false)

  const referralCode = profile?.referral_code || ''
  const referralLink = referralCode ? `${BASE_URL}/join?ref=${referralCode}` : ''

  useEffect(() => {
    if (!session?.user?.id) return
    fetchReferrals()
  }, [session])

  async function fetchReferrals() {
    const { data } = await supabase
      .from('referrals')
      .select('*')
      .eq('referrer_id', session.user.id)
      .order('created_at', { ascending: false })
    if (data) setReferrals(data)
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(referralLink)
      setCopied(true)
      setTimeout(() => setCopied(false), 2500)
    } catch {
      // Fallback for older browsers
      const el = document.createElement('textarea')
      el.value = referralLink
      document.body.appendChild(el)
      el.select()
      document.execCommand('copy')
      document.body.removeChild(el)
      setCopied(true)
      setTimeout(() => setCopied(false), 2500)
    }
  }

  const rewards = {
    gold:     'Refer 1 friend → 1 free week at Premium',
    premium:  'Refer 2 friends → 1 free month at Premium',
    platinum: 'Refer 3 friends → Free dinner booking credit'
  }

  const tierTarget = {
    gold: 1, premium: 2, platinum: 3
  }

  const progress = Math.min(referrals.length, tierTarget[tier] || 1)
  const target = tierTarget[tier] || 1

  return (
    <div className="referral">
      <div className="referral__header">
        <p className="referral__eyebrow">Refer & Earn</p>
        <h3 className="referral__title">Invite Friends to the Circle</h3>
        <p className="referral__sub">{rewards[tier]}</p>
      </div>

      {/* REFERRAL LINK */}
      <div className="referral__link-wrap">
        <div className="referral__link-box">
          <span className="referral__link-text">{referralLink || 'Generating your link...'}</span>
        </div>
        <button
          className={`btn btn--sm ${copied ? 'btn--ghost' : 'btn--outline'}`}
          onClick={copyLink}
          disabled={!referralLink}
        >
          {copied ? 'Copied!' : 'Copy Link'}
        </button>
      </div>

      {/* PROGRESS */}
      <div className="referral__progress">
        <div className="referral__progress-header">
          <span className="referral__progress-label">
            {referrals.length}/{target} referrals to unlock your reward
          </span>
          <span className="referral__progress-count">{referrals.length} referred</span>
        </div>
        <div className="referral__bar">
          <div
            className="referral__bar-fill"
            style={{ width: `${(progress / target) * 100}%` }}
          />
        </div>
        {referrals.length >= target && (
          <div className="referral__reward-unlocked">
            Reward unlocked! Contact Sky Bri to claim it.
          </div>
        )}
      </div>

      {/* REFERRAL LIST */}
      {referrals.length > 0 && (
        <div className="referral__list">
          <p className="referral__list-label">Your referrals</p>
          {referrals.map(r => (
            <div key={r.id} className="referral__item">
              <span className="referral__item-email">{r.referred_email}</span>
              <span className={`referral__item-status ${r.referred_user_id ? 'status--joined' : 'status--pending'}`}>
                {r.referred_user_id ? 'Joined' : 'Pending'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
